import React from 'react';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { OAuthStatus } from '../../../../types/email';
import { badgeBaseClass, badgeTones } from '../../../ui/StatusBadge';
import { Skeleton } from '../../../ui/skeleton';
import { cn } from '../../../../DashboardPage/lib/utils';
import Button from '../../../ui/Button';

interface OAuthConnectionStatusProps {
  ConnectionStatus: OAuthStatus;
  onConnectGmail: () => void;
  onConnectOutlook: () => void;
  onDisconnect: () => void;
  connecting?: 'gmail' | 'outlook' | null;
  /** True while the status request is in flight, before either state is known. */
  loading?: boolean;
}

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const GmailIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.908 1.528-1.147C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
  </svg>
);

const OutlookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 7.387v10.478c0 .23-.08.427-.24.59a.806.806 0 0 1-.59.239h-9.952V9.975l-1.64 1.552-1.64-1.552v8.719H1.83a.806.806 0 0 1-.59-.24.806.806 0 0 1-.24-.59V7.387c0-.459.2-.81.6-1.054l5.53-4.147c.363-.27.752-.27 1.166 0l2.703 2.027 2.703-2.027c.414-.27.803-.27 1.166 0l5.53 4.147c.4.245.6.595.6 1.054zM9.958 12l-5.6-5.294v10.588L9.958 12z" fill="#0072C6"/>
  </svg>
);

const OAuthConnectionStatus: React.FC<OAuthConnectionStatusProps> = ({
  ConnectionStatus,
  onConnectGmail,
  onConnectOutlook,
  onDisconnect,
  connecting = null,
  loading = false,
}) => {
  const gmail = ConnectionStatus?.gmail;
  const outlook = ConnectionStatus?.outlook;
  const activeProvider = gmail?.connected ? 'gmail' : outlook?.connected ? 'outlook' : null;

  /*
    Until the status arrives, neither branch below is knowable — a disconnected
    status and an unfetched one look identical. Shaped like the connected card
    (the steadier of the two states) so the panel keeps its height and the real
    content replaces it in place.
  */
  if (loading) {
    return (
      <div aria-busy="true">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full shrink-0" />
            <div className="min-w-0 space-y-2">
              <Skeleton className="w-32 h-4" />
              <Skeleton className="h-3.5 w-48" />
              <Skeleton className="w-40 h-3" />
            </div>
          </div>
          <Skeleton className="w-24 h-9 rounded-md shrink-0" />
        </div>
      </div>
    );
  }

  if (!activeProvider) {
    const isAnyConnecting = connecting !== null;

    return (
      <div className="p-6 border-2 border-primary-200 rounded-lg bg-primary-50">
        <div className="flex flex-col items-center text-center gap-4">
          <XCircleIcon className="w-12 h-12 text-primary-700" />
          <div>
            <h3 className="text-lg font-semibold text-primary-900 mb-2">
              Connect Your Email Account
            </h3>
            <p className="text-sm text-primary-800 mb-4">
              Connect your Gmail or Microsoft Outlook account to send pitches directly to editors
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="neutral"
              onClick={onConnectGmail}
              disabled={isAnyConnecting}
            >
              {connecting === 'gmail' ? <Spinner /> : <GmailIcon />}
              {connecting === 'gmail' ? 'Connecting...' : 'Connect Gmail'}
            </Button>
            <Button
              variant="neutral"
              onClick={onConnectOutlook}
              disabled={isAnyConnecting}
            >
              {connecting === 'outlook' ? <Spinner /> : <OutlookIcon />}
              {connecting === 'outlook' ? 'Connecting...' : 'Connect Outlook'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const active = activeProvider === 'gmail' ? gmail : outlook;
  const providerLabel = activeProvider === 'gmail' ? 'Gmail' : 'Outlook';
  const otherLabel = activeProvider === 'gmail' ? 'Outlook' : 'Gmail';

  /*
    A steady connected state, not an alert — so the card stays neutral and the
    success signal is carried by a single badge. Washing the whole panel green
    put the provider's own brand mark (Gmail's red) on a green ground and set it
    against the red Disconnect button, which read as a clash rather than a status.
  */
  return (
    // No card chrome of its own: this renders inside a SettingsPanel, and its
    // own border made a card-in-a-card.
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 border rounded-full shrink-0 bg-neutral-50 border-neutral-200">
            {activeProvider === 'gmail' ? <GmailIcon /> : <OutlookIcon />}
          </div>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-black">
              {providerLabel}
              <span className={cn(badgeBaseClass, badgeTones.success)}>Connected</span>
            </p>
            <p className="text-sm truncate text-neutral-700">{active.email}</p>
            {active.connectedAt && (
              <p className="mt-1 text-xs text-neutral-500">
                Connected since: {new Date(active.connectedAt).toLocaleDateString()}
              </p>
            )}
            {active.lastRefreshedAt && (
              <p className="text-xs text-neutral-500">
                Last refreshed: {new Date(active.lastRefreshedAt).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
            <p className="mt-1 text-xs text-neutral-500">
              Want to use {otherLabel} instead? Disconnect first, then reconnect.
            </p>
          </div>
        </div>
        <Button
          variant="dangerOutline"
          onClick={onDisconnect}
          className="shrink-0"
        >
          Disconnect
        </Button>
      </div>
    </div>
  );
};

export default OAuthConnectionStatus;
